import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { Button } from 'primeng/button';
import { Select } from "primeng/select";
import { FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ModelService } from '../../../../../services/vehicles/model-service';
import { Model } from '../../../../../models/model';
import { PricingPlansService } from '../../../../../services/setting/plans/pricing-plans-service';
import { PricingPlans } from '../../../../../models/pricing-plans';
import { MileageService } from '../../../../../services/setting/plans/mileage-service';
import { MileagePlans } from '../../../../../models/mileage-plans';
import { enumToOptions } from '../../../../utility/enumToOptions';
import { BodyType, fuelLevelUnit, FuelType, RentalType, TransmissionType } from '../../../../../models/enums';
import { InputNumber } from 'primeng/inputnumber';
import { MultiSelect } from 'primeng/multiselect';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-add-model-form',
  imports: [ReactiveFormsModule, Select, Button, FileUploadModule, InputNumber, MultiSelect, AutoCompleteModule, InputText],
  templateUrl: './add-model-form.html',
  styleUrl: './add-model-form.scss'
})
export class AddModelForm implements OnInit {

  @ViewChild('fileUpload') fileUpload!: FileUpload;

  modelForm: FormGroup
  file: File | undefined
  loading = false
  isSubmitted = false
  editMode = false
  modelId: string
  pricingPlansList = signal<PricingPlans[]>([])
  mileagePlansList = signal<MileagePlans[]>([])

  bodyTypeOptions = enumToOptions(BodyType);
  fuelTypeOptions = enumToOptions(FuelType);
  transmissionTypeOptions = enumToOptions(TransmissionType);
  rentalTypeOptions = enumToOptions(RentalType);
  fuelLevelUnitOptions = enumToOptions(fuelLevelUnit);

  modelData: Partial<Model> = {};
  imagePreview: string | ArrayBuffer | null = null;

  fileError = false
  formErrors: Record<string, string> = {};

  private modelService = inject(ModelService)
  private pricingPlansService = inject(PricingPlansService)
  private mileageService = inject(MileageService)
  private formBuilder = inject(FormBuilder)
  private activatedRoute = inject(ActivatedRoute)
  private router = inject(Router)

  ngOnInit() {
    this.getPricingPlans()
    this.getMileagePlans()
    this.initModelForm()
    this.checkEditMode()
  }

  private initModelForm = () => {
    this.modelForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      maker: ['', [Validators.required]],
      bodyType: [undefined, [Validators.required]],
      fuelType: [undefined, [Validators.required]],
      transmissionType: [undefined, [Validators.required]],
      rentalType: [undefined, [Validators.required]],
      seats: [null, [Validators.required]],
      horsepower: [null, [Validators.required]],
      maxSpeed: [null, [Validators.required]],
      tankCapacity: [null, [Validators.required]],
      kmPerLiter: [null, [Validators.required]],
      zeroToHundred: [null, [Validators.required]],
      features: [[]],
      startingFee: [null, [Validators.required]],
      pricingPlanIds: [[], [Validators.required]],
      mileagePlanIds: [[], [Validators.required]],
      tempHold: [null, [Validators.required]],
      overageRatePerKm: [null, [Validators.required]],
      fuelLevelUnit: ["PERCENTAGE", [Validators.required]]
    });
  }

  private checkEditMode = () => {
    this.activatedRoute.params.subscribe(params => {
      if (params['id']) {
        this.editMode = true
        this.modelId = params['id']
        this.modelService.getModelById(this.modelId).subscribe({
          next: (res) => {
            const model = res.data;
            this.modelData = { ...model }
            this.modelForm.patchValue({
              name: model.name,
              maker: model.maker,
              bodyType: model.bodyType,
              fuelType: model.fuelType,
              transmissionType: model.transmissionType,
              rentalType: model.rentalType,
              seats: model.seats,
              horsepower: model.horsepower,
              maxSpeed: model.maxSpeed,
              tankCapacity: model.tankCapacity,
              kmPerLiter: model.kmPerLiter,
              zeroToHundred: model.zeroToHundred,
              features: model.features,
              startingFee: model.startingFee,
              pricingPlanIds: model.pricingPlanIds,
              mileagePlanIds: model.mileagePlanIds,
              tempHold: model.tempHold,
              overageRatePerKm: model.overageRatePerKm,
              fuelLevelUnit: model.fuelLevelUnit
            });

            this.file = undefined; // initially no new file
          }
        })
      }
    })
  }

  private addModel = (payload: FormData) => {
    this.modelService.addModel(payload).subscribe({
      next: () => {
        this.isSubmitted = false
        this.loading = false
        this.modelForm.reset()
      },
      error: (err) => {
        this.loading = false
        if (err.status === 400) {
          const errorData = err.error.data
          this.formErrors = { ...errorData }
        }
      }
    })
  }

  private editModel = (payload: FormData) => {
    this.modelService.updateModel(parseInt(this.modelId), payload).subscribe({
      next: () => {
        this.isSubmitted = false
        this.loading = false
        this.router.navigate(['/vehicles'], { queryParams: { tab: 'models' } })
      },
      error: (err) => {
        this.loading = false
        if (err.status === 400) {
          const errorData = err.error.data
          this.formErrors = { ...errorData }
        }
      }
    })
  }

  onSubmit = () => {
    this.isSubmitted = true
    this.loading = true
    this.formErrors = {}
    if (this.modelForm.invalid) {
      this.loading = false
      return;
    }

    // Additional check for file only in Add Mode
    if (!this.editMode && !this.file) {
      this.loading = false
      this.fileError = true
      return;
    }
    const payload = new FormData();
    const formValue = this.modelForm.value

    const data: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(formValue)) {
      if (value !== null && value !== undefined) {
        data[key] = value;
      }
    }
    payload.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));
    if (this.file) {
      payload.append('image', this.file);
    }
    if (this.editMode) {
      this.editModel(payload);
    } else {
      this.addModel(payload);
    }
  };

  onImageSelect = (event: FileSelectEvent) => {
    const file = event.files?.[0];
    if (file) {
      this.file = file;

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  getPricingPlans = () => {
    this.pricingPlansService.getPricingPlansForDropdown().subscribe({
      next: (res) => {
        this.pricingPlansList.set(res.data)
      }
    })
  }

  getMileagePlans = () => {
    this.mileageService.getMileagePlansForDropdown().subscribe({
      next: (res) => {
        this.mileagePlansList.set(res.data)
      }
    })
  }

  openBrowser = () => {
    this.fileUpload.choose();
  }

  removeImage = () => {
    this.imagePreview = null;
    this.modelData.imageUrl = undefined;
    this.file = undefined;
    this.fileUpload.clear();
    this.fileError = false;
  };

  goBack = () => {
    this.router.navigate(['/vehicles'], { queryParams: { tab: 'models' } });
  }

  onCancel = () => {
    // if (this.editMode) {
    //   this.router.navigate(['/vehicles'], { queryParams: { tab: 'models' } });
    // } else {
      this.router.navigate(['/vehicles'], { queryParams: { tab: 'models' } });
      this.modelForm.reset()
      this.modelForm.controls['fuelLevelUnit'].setValue("PERCENTAGE")
      this.removeImage()
    // }
  }
}
